import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, DrawingManager, Polygon } from '@react-google-maps/api';
import { TextField, Button, Box, Paper, Typography, CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';
import { IGeoFence } from '../../models/GeoFence';

const libraries: ("drawing" | "geometry" | "places" | "visualization")[] = ["drawing"];

const mapContainerStyle = {
    width: '100%',
    height: '600px'
};

const center = {
    lat: 20.5937, // Default center (India)
    lng: 78.9629
};

const Fence: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [coordinates, setCoordinates] = useState<google.maps.LatLngLiteral[]>([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
        libraries
    });

    const polygonRef = useRef<google.maps.Polygon | null>(null);
    const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);

    useEffect(() => {
        if (id && id !== 'new') {
            setFetching(true);
            fetch(`${process.env.REACT_APP_API_URL}/geo-fence/${id}`)
                .then(res => res.json())
                .then(json => {
                    if (json.success && json.geoFence) {
                        setName(json.geoFence.geo_fence_name);
                        const data = json.geoFence.geo_fence_data;
                        if (data && data.coordinates) {
                            setCoordinates(data.coordinates);
                        }
                    }
                })
                .catch(err => console.error("Error fetching fence:", err))
                .finally(() => setFetching(false));
        }
    }, [id]);

    const onPolygonComplete = useCallback((polygon: google.maps.Polygon) => {
        if (polygonRef.current) {
            polygonRef.current.setMap(null);
        }

        const path = polygon.getPath();
        const coords: google.maps.LatLngLiteral[] = [];
        for (let i = 0; i < path.getLength(); i++) {
            const point = path.getAt(i);
            coords.push({ lat: point.lat(), lng: point.lng() });
        }
        setCoordinates(coords);
        polygonRef.current = polygon;

        // Disable drawing mode after completion
        if (drawingManagerRef.current) {
            drawingManagerRef.current.setDrawingMode(null);
        }
    }, []);

    const onEditPolygon = useCallback(() => {
        if (polygonRef.current) {
            const path = polygonRef.current.getPath();
            const coords: google.maps.LatLngLiteral[] = [];
            for (let i = 0; i < path.getLength(); i++) {
                const point = path.getAt(i);
                coords.push({ lat: point.lat(), lng: point.lng() });
            }
            setCoordinates(coords);
        }
    }, []);

    const handleSave = async () => {
        if (!name) {
            Swal.fire('Error', 'Please enter a fence name', 'error');
            return;
        }
        if (coordinates.length === 0) {
            Swal.fire('Error', 'Please draw a fence on the map', 'error');
            return;
        }

        setLoading(true);
        const url = id && id !== 'new'
            ? `${process.env.REACT_APP_API_URL}/geo-fence/${id}`
            : `${process.env.REACT_APP_API_URL}/geo-fence`;
        const method = id && id !== 'new' ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    geo_fence_name: name,
                    geo_fence_data: { coordinates }
                })
            });
            const json = await response.json();
            if (json.success) {
                Swal.fire('Success', 'Geo Fence saved successfully', 'success');
                navigate('/fences');
            } else {
                Swal.fire('Error', json.message || 'Failed to save', 'error');
            }
        } catch (error) {
            console.error("Save error:", error);
            Swal.fire('Error', 'An error occurred while saving', 'error');
        } finally {
            setLoading(false);
        }
    };

    const clearMap = () => {
        if (polygonRef.current) {
            polygonRef.current.setMap(null);
            polygonRef.current = null;
        }
        setCoordinates([]);
    };

    if (loadError) return <div>Error loading maps</div>;
    if (!isLoaded || fetching) return <Box display="flex" justifyContent="center" p={10}><CircularProgress /></Box>;

    return (
        <Box p={3}>
            <div className="content-header pb-6 flex justify-between items-center">
                <div className="content-header-label">
                    {id === 'new' ? 'Add New Geo Fence' : 'Edit Geo Fence'}
                </div>
                <Box display="flex" gap={2}>
                    <Button variant="outlined" onClick={() => navigate('/fences')}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
                        {loading ? 'Saving...' : 'Save Fence'}
                    </Button>
                </Box>
            </div>

            {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff3e0' }}>
                    <Typography color="warning.main">
                        <strong>Warning:</strong> Google Maps API Key is missing. Please add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to your environment variables.
                    </Typography>
                </Paper>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <TextField
                    fullWidth
                    label="Geo Fence Name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mb: 3 }}
                />

                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">
                        Use the drawing tool (Polygon) to define the fence area.
                    </Typography>
                    <Button size="small" variant="text" color="error" onClick={clearMap}>Clear Drawing</Button>
                </Box>

                <Box sx={{ border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={coordinates.length > 0 ? coordinates[0] : center}
                        zoom={coordinates.length > 0 ? 15 : 5}
                    >
                        <DrawingManager
                            onLoad={manager => drawingManagerRef.current = manager}
                            onPolygonComplete={onPolygonComplete}
                            options={{
                                drawingControl: true,
                                drawingControlOptions: {
                                    position: google.maps.ControlPosition.TOP_CENTER,
                                    drawingModes: [google.maps.drawing.OverlayType.POLYGON]
                                },
                                polygonOptions: {
                                    fillColor: '#2196f3',
                                    fillOpacity: 0.3,
                                    strokeWeight: 2,
                                    clickable: true,
                                    editable: true,
                                    zIndex: 1
                                }
                            }}
                        />
                        {coordinates.length > 0 && id !== 'new' && !polygonRef.current && (
                            <Polygon
                                paths={coordinates}
                                options={{
                                    fillColor: '#2196f3',
                                    fillOpacity: 0.3,
                                    strokeWeight: 2,
                                    clickable: true,
                                    editable: true,
                                    draggable: true,
                                }}
                                onMouseUp={onEditPolygon}
                                onDragEnd={onEditPolygon}
                                onLoad={poly => polygonRef.current = poly}
                            />
                        )}
                    </GoogleMap>
                </Box>
            </Paper>
        </Box>
    );
};

export default Fence;
